import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Box,
  MenuItem,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import SavingOverlay from '../SavingOverlay';
import { colors, typography } from '../../theme/designSystem';
import {
  patientEvolutionsService,
  mapPatientEvolutionError,
  CatalogOption,
  QuickCreatedCatalogItem,
  CATALOG_NAME_MIN_LENGTH,
  CATALOG_NAME_MAX_LENGTH,
} from '../../services/patientEvolutionsService';

export type QuickAddKind = 'approach' | 'resource';

interface Props {
  kind: QuickAddKind;
  open: boolean;
  /** tipos de recurso (só para `resource`) */
  resourceTypes: CatalogOption[];
  onClose: () => void;
  onSaved: (item: QuickCreatedCatalogItem) => void;
}

type Visibility = '' | 'clinic' | 'me';

/**
 * Cadastro rápido de abordagem/recurso sem sair da evolução. "Visível para" não tem padrão:
 * quem cadastra decide se o item vale para toda a clínica ou só para si.
 */
const EvolutionCatalogQuickAdd: React.FC<Props> = ({ kind, open, resourceTypes, onClose, onSaved }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [name, setName] = useState('');
  const [resourceTypeId, setResourceTypeId] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isResource = kind === 'resource';
  const title = isResource ? 'Novo recurso' : 'Nova abordagem';
  const trimmed = name.trim();
  const canSave = trimmed.length >= CATALOG_NAME_MIN_LENGTH
    && (!isResource || resourceTypeId !== '')
    && visibility !== '';

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (trimmed.length < CATALOG_NAME_MIN_LENGTH) {
      setError(`Informe o nome (mínimo de ${CATALOG_NAME_MIN_LENGTH} caracteres).`);
      return;
    }
    if (isResource && !resourceTypeId) {
      setError('Selecione o tipo do recurso.');
      return;
    }
    if (!visibility) {
      setError('Escolha para quem o item fica visível.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const onlyForMe = visibility === 'me';
      const item = isResource
        ? await patientEvolutionsService.quickCreateResource(trimmed, resourceTypeId, onlyForMe)
        : await patientEvolutionsService.quickCreateApproach(trimmed, onlyForMe);
      onSaved(item);
    } catch (err: any) {
      setError(mapPatientEvolutionError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : '12px', position: 'relative', overflow: 'hidden' } }}
    >
      <SavingOverlay open={saving} message="Cadastrando..." />
      <DialogTitle
        sx={{
          backgroundColor: colors.primary,
          color: colors.white,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.25rem',
        }}
      >
        <Typography component="h3" sx={{ fontSize: '1.1rem', fontWeight: typography.fontWeight.semibold, margin: 0 }}>
          {title}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: colors.white, padding: '0.25rem' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '1.25rem !important', paddingTop: '1.5rem !important' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Box sx={{ backgroundColor: '#fde2e4', color: colors.error, borderRadius: '8px', padding: '0.6rem 1rem', fontSize: '0.85rem' }}>
              {error}
            </Box>
          )}
          <TextField
            label="Nome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
            size="small"
            inputProps={{ maxLength: CATALOG_NAME_MAX_LENGTH }}
            InputLabelProps={{ shrink: true }}
          />
          {isResource && (
            <TextField
              select
              label="Tipo"
              value={resourceTypeId}
              onChange={(e) => setResourceTypeId(e.target.value)}
              required
              size="small"
              InputLabelProps={{ shrink: true }}
            >
              <MenuItem value="">
                <em>Selecione</em>
              </MenuItem>
              {resourceTypes.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </TextField>
          )}
          <FormControl required>
            <FormLabel sx={{ fontSize: '0.85rem', color: colors.text, '&.Mui-focused': { color: colors.text } }}>
              Visível para
            </FormLabel>
            <RadioGroup value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)}>
              <FormControlLabel
                value="clinic"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: colors.primary } }} />}
                label="Toda a clínica"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
              />
              <FormControlLabel
                value="me"
                control={<Radio size="small" sx={{ '&.Mui-checked': { color: colors.primary } }} />}
                label="Só para mim"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.9rem' } }}
              />
            </RadioGroup>
            <Typography sx={{ fontSize: '0.78rem', color: colors.textSecondary }}>
              "Só para mim" aparece apenas nas suas evoluções; "Toda a clínica" fica disponível para todos.
            </Typography>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '1rem 1.25rem' }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={saving}
          sx={{ textTransform: 'none', color: '#6c757d', borderColor: '#6c757d' }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || !canSave}
          sx={{ textTransform: 'none', backgroundColor: colors.primary }}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvolutionCatalogQuickAdd;
